import React, { useEffect, useState } from 'react';
import { X, Download, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import { buildInvoicePDF } from '../utils/exportHelpers';

// A quick look at an invoice as the client would get it, without downloading
// anything: the same PDF the Tax Invoice button makes, shown in the page.
// Looking is not "taking a PDF for the client", so it records no print and
// never locks the invoice; the Download button here does both, as always.
export default function InvoicePreviewModal({ invoiceId, onClose, onDownloaded }) {
  const [url, setUrl] = useState(null);
  const [inv, setInv] = useState(null);
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true, blobUrl = null;
    (async () => {
      try {
        const [{ data: full }, { data: settings }] = await Promise.all([
          api.get(`/billing/invoices/${invoiceId}`),
          api.get('/settings/billing'),
        ]);
        const pdf = await buildInvoicePDF(full, settings || {});
        if (!alive) return;
        blobUrl = pdf.output('bloburl');
        setDoc(pdf); setInv(full); setUrl(blobUrl);
      } catch (e) {
        if (alive) setError(e?.response?.data?.error || 'Could not build the invoice');
      }
    })();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => { alive = false; document.removeEventListener('keydown', onKey); if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [invoiceId, onClose]);

  const download = async () => {
    if (!doc || !inv) return;
    doc.save(`${inv.invoice_number || 'draft-invoice'}.pdf`);
    if (inv.status !== 'draft' && inv.status !== 'cancelled') {
      await api.post(`/billing/invoices/${inv.id}/printed`).catch(() => {});
      onDownloaded?.();
    }
    toast.success('Downloaded');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 sm:p-6" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 py-2.5 border-b">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-gray-800 truncate">
              {inv ? `${inv.status === 'draft' ? 'Draft invoice' : `Invoice ${inv.invoice_number}`} · ${inv.client_name}` : 'Invoice preview'}
            </p>
            {inv && <p className="text-[11px] text-gray-400">Preview only — nothing is recorded until you download, email or WhatsApp it.</p>}
          </div>
          <button type="button" onClick={download} disabled={!doc}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-40">
            <Download size={13} /> Download PDF
          </button>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700" title="Close (Esc)"><X size={20} /></button>
        </div>
        <div className="flex-1 bg-gray-100">
          {error ? (
            <p className="p-6 text-sm text-red-600">{error}</p>
          ) : url ? (
            <iframe title="Invoice preview" src={`${url}#toolbar=0&navpanes=0`} className="w-full h-full border-0" />
          ) : (
            <div className="h-full flex items-center justify-center text-gray-400 text-sm"><Loader2 size={18} className="animate-spin mr-2" /> Building the invoice…</div>
          )}
        </div>
      </div>
    </div>
  );
}
